import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  iosNativeDocumentPage,
  iosNativeScreenSubtitle,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';

const DocumentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 2rem;
  background: white;
  color: ${theme.colors.text.primary};
  font-family: ${theme.typography.fontFamily.primary};
  line-height: ${theme.typography.lineHeight.relaxed};

  @media print {
    padding: 2rem;
    max-width: 100%;
  }

  ${iosNativeDocumentPage}
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  text-align: center;

  body.native-app & {
    text-align: left;
  }

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  text-align: center;
  margin-bottom: 3rem;

  body.native-app & {
    text-align: left;
  }

  ${iosNativeScreenSubtitle}
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 1rem;
  border-bottom: 2px solid ${theme.colors.border.light};
  padding-bottom: 0.5rem;
`;

const SectionSubtitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
`;

const Paragraph = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  margin-bottom: 1rem;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const List = styled.ul`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
  font-size: ${theme.typography.fontSize.sm};
`;

const TableHeader = styled.thead`
  background: ${theme.colors.background.secondary};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.border.light};
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const TableCell = styled.td`
  padding: 0.75rem;
  color: ${theme.colors.text.secondary};
  border-bottom: 1px solid ${theme.colors.border.light};
`;

const HighlightBox = styled.div`
  background: ${theme.colors.background.secondary};
  border-left: 4px solid ${theme.colors.primary[500]};
  padding: 1rem;
  margin: 1.5rem 0;
  border-radius: ${theme.borderRadius.md};
`;

const HighlightText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.primary};
  margin: 0;
  font-weight: ${theme.typography.fontWeight.medium};
`;

const PricingStrategyPage: React.FC = () => {
  return (
    <DocumentContainer>
      <Title>GlowLens Monetization Strategy</Title>
      <Subtitle>Internal Document — Pricing, Positioning & Upgrade Paths</Subtitle>

      <Section>
        <SectionTitle>1. Overview</SectionTitle>
        <Paragraph>
          GlowLens operates on a freemium model with four distinct tiers designed to serve users at different stages of their skincare journey. Our monetization strategy focuses on value-based pricing, clear feature differentiation, and strategic upgrade moments that align with user needs and engagement levels.
        </Paragraph>
        <Paragraph>
          <strong>Target Conversion Rate:</strong> 12-18% of free users upgrade to paid plans within their first 30 days. This benchmark is based on industry standards for freemium wellness apps with strong product-market fit.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>2. Target Audience & Value Ladder</SectionTitle>
        
        <SectionSubtitle>Free Plan</SectionSubtitle>
        <Paragraph>
          <strong>Target:</strong> New users, curious browsers, price-sensitive users
        </Paragraph>
        <List>
          <ListItem>Try the app with basic acne guidance</ListItem>
          <ListItem>Limited scans (3 per week) to experience core value</ListItem>
          <ListItem>Basic analysis to understand skin health</ListItem>
          <ListItem>3 DIY mask recipes to get started</ListItem>
        </List>
        <Paragraph>
          <strong>Goal:</strong> Hook users with core value, create habit of scanning, demonstrate AI capabilities. Free users should feel the app works but want more.
        </Paragraph>

        <SectionSubtitle>GlowLens Plus ($4.99/month)</SectionSubtitle>
        <Paragraph>
          <strong>Target:</strong> Students, teens, budget-conscious users who are serious about skincare
        </Paragraph>
        <List>
          <ListItem>Unlimited scans for consistent tracking</ListItem>
          <ListItem>Advanced acne analysis with detailed insights</ListItem>
          <ListItem>10 DIY mask recipes (5 upfront + 5 additional)</ListItem>
          <ListItem>Custom diet plan based on scan results</ListItem>
          <ListItem>Food scanner at home (2 scans/day limit)</ListItem>
          <ListItem>Progress tracking to see improvements over time</ListItem>
        </List>
        <Paragraph>
          <strong>Goal:</strong> Remove scan limits, provide enough value for serious users, position as "student-friendly" premium option. This is our "Most Popular" tier.
        </Paragraph>

        <SectionSubtitle>GlowLens Pro ($14.99/month)</SectionSubtitle>
        <Paragraph>
          <strong>Target:</strong> Skincare enthusiasts, users who want full routine optimization, diet coaching, and advanced tracking
        </Paragraph>
        <List>
          <ListItem>Everything in Plus</ListItem>
          <ListItem>Most Advanced acne analysis with precision tracking</ListItem>
          <ListItem>Unlimited DIY masks</ListItem>
          <ListItem>Advanced dietary plan with smarter food scoring</ListItem>
          <ListItem>Restaurant food scanner (unlimited)</ListItem>
          <ListItem>Habit tracking (sleep, water, stress)</ListItem>
          <ListItem>Weekly skin insights and analytics</ListItem>
        </List>
        <Paragraph>
          <strong>Goal:</strong> Serve power users who want comprehensive skincare optimization. Position as "complete skincare system."
        </Paragraph>

        <SectionSubtitle>GlowLens Elite ($69.99/month)</SectionSubtitle>
        <Paragraph>
          <strong>Target:</strong> Severe acne sufferers, prescription users, parents managing teen skincare, users who want expert-level support
        </Paragraph>
        <List>
          <ListItem>Everything in Pro</ListItem>
          <ListItem>Tele-dermatology video consult (1x/month)</ListItem>
          <ListItem>Prescription-aware guidance with ingredient conflict warnings</ListItem>
          <ListItem>Priority AI processing (Lightning GlowScan™)</ListItem>
          <ListItem>Deep-dive progress reports (monthly PDF exports)</ListItem>
          <ListItem>Family plan (up to 3 linked sub-accounts)</ListItem>
          <ListItem>VIP support (24-hour email response SLA)</ListItem>
          <ListItem>Derm-reviewed routines</ListItem>
        </List>
        <Paragraph>
          <strong>Goal:</strong> Premium tier for users with complex needs who value expert guidance and family-friendly pricing. Position as "dermatologist-level support without the waiting room."
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>3. Feature Matrix</SectionTitle>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Feature</TableHeaderCell>
              <TableHeaderCell>Free</TableHeaderCell>
              <TableHeaderCell>Plus</TableHeaderCell>
              <TableHeaderCell>Pro</TableHeaderCell>
              <TableHeaderCell>Elite</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <tbody>
            <TableRow>
              <TableCell>Monthly scans</TableCell>
              <TableCell>3/week</TableCell>
              <TableCell>Unlimited</TableCell>
              <TableCell>Unlimited</TableCell>
              <TableCell>Unlimited</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Acne analysis</TableCell>
              <TableCell>Basic</TableCell>
              <TableCell>Advanced</TableCell>
              <TableCell>Most Advanced</TableCell>
              <TableCell>Most Advanced</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>DIY mask library</TableCell>
              <TableCell>3 masks</TableCell>
              <TableCell>10 masks</TableCell>
              <TableCell>Unlimited</TableCell>
              <TableCell>Unlimited</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Custom diet plan</TableCell>
              <TableCell>—</TableCell>
              <TableCell>✓</TableCell>
              <TableCell>✓ Advanced</TableCell>
              <TableCell>✓ Advanced</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Restaurant food scanner</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>✓</TableCell>
              <TableCell>✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Progress tracking</TableCell>
              <TableCell>—</TableCell>
              <TableCell>✓</TableCell>
              <TableCell>✓</TableCell>
              <TableCell>✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Habit tracking</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>✓</TableCell>
              <TableCell>✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Weekly insights</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>✓</TableCell>
              <TableCell>✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Export reports</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Tele-dermatology</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>1x/month</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Prescription guidance</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Priority processing</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Family plan</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>3 accounts</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>VIP support</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell>24hr response</TableCell>
            </TableRow>
          </tbody>
        </Table>
      </Section>

      <Section>
        <SectionTitle>4. Pricing Rationale</SectionTitle>
        
        <SectionSubtitle>Free Plan</SectionSubtitle>
        <Paragraph>
          Free forever to maximize user acquisition and demonstrate value. Limited scans create urgency without feeling restrictive. 3 scans/week is enough to see progress but encourages upgrade for serious users.
        </Paragraph>

        <SectionSubtitle>Plus: $4.99/month ($39.99/year)</SectionSubtitle>
        <Paragraph>
          Positioned as "student-friendly" premium tier. Price point is low enough to be impulse-buy territory but high enough to filter serious users. Yearly pricing offers 33% savings to improve LTV. This is our volume tier—most conversions should land here.
        </Paragraph>

        <SectionSubtitle>Pro: $14.99/month ($99.99/year)</SectionSubtitle>
        <Paragraph>
          Targets users who want comprehensive skincare optimization. Price reflects value of advanced features, habit tracking, and unlimited access. Yearly pricing offers 44% savings. This tier should appeal to skincare enthusiasts and power users.
        </Paragraph>

        <SectionSubtitle>Elite: $69.99/month ($699.99/year)</SectionSubtitle>
        <Paragraph>
          Premium tier for complex needs. Price reflects value of tele-dermatology, prescription guidance, and family plan. Yearly pricing offers "2 months free" (17% savings). This tier targets users with severe acne, prescription routines, or families who want expert-level support.
        </Paragraph>

        <HighlightBox>
          <HighlightText>
            <strong>Yearly Pricing Strategy:</strong> All paid plans offer significant yearly discounts (33-44%) to improve customer lifetime value and reduce churn. Yearly plans also reduce payment processing costs and improve cash flow.
          </HighlightText>
        </HighlightBox>
      </Section>

      <Section>
        <SectionTitle>5. Upgrade Paths & Nudge Moments</SectionTitle>
        
        <SectionSubtitle>Key Upgrade Triggers</SectionSubtitle>
        <List>
          <ListItem><strong>After 3 scans:</strong> User has experienced core value, show paywall for unlimited scans</ListItem>
          <ListItem><strong>After major score improvement:</strong> User sees progress, offer Pro for advanced tracking</ListItem>
          <ListItem><strong>Trying to save &gt;3 diet plans:</strong> Free users hit limit, offer Plus/Pro</ListItem>
          <ListItem><strong>Accessing locked feature:</strong> Context-aware paywall (e.g., "Unlock your full GlowDiet™ plan")</ListItem>
          <ListItem><strong>After first week:</strong> Onboarding complete, show plan comparison</ListItem>
          <ListItem><strong>Hitting scan limit:</strong> Clear message with upgrade CTA</ListItem>
          <ListItem><strong>Viewing results detail:</strong> Show "Upgrade to see advanced analysis" for free users</ListItem>
        </List>

        <SectionSubtitle>Upgrade Flow Recommendations</SectionSubtitle>
        <Paragraph>
          <strong>Free → Plus:</strong> Emphasize unlimited scans, advanced analysis, and DIY mask library. Position as "remove limits, unlock your full potential."
        </Paragraph>
        <Paragraph>
          <strong>Free/Plus → Pro:</strong> Emphasize most advanced analysis, restaurant scanner, habit tracking, and comprehensive skincare system. Position as "complete skincare optimization."
        </Paragraph>
        <Paragraph>
          <strong>Any → Elite:</strong> Emphasize expert support, tele-dermatology, prescription guidance, and family plan. Position as "dermatologist-level support without the waiting room."
        </Paragraph>

        <SectionSubtitle>Paywall Best Practices</SectionSubtitle>
        <List>
          <ListItem>Context-aware messaging that references the blocked feature</ListItem>
          <ListItem>Show 3 key benefits tailored to the context</ListItem>
          <ListItem>Side-by-side comparison of current vs recommended plan</ListItem>
          <ListItem>Monthly/yearly toggle for pricing flexibility</ListItem>
          <ListItem>Trust elements: "7-day free trial, cancel anytime"</ListItem>
          <ListItem>Two clear CTAs: primary (upgrade) and secondary (continue free)</ListItem>
          <ListItem>Smooth animations to feel premium</ListItem>
          <ListItem>Exit options: clicking outside or "X" returns to limited view</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>6. Conversion Optimization</SectionTitle>
        
        <SectionSubtitle>Onboarding Funnel</SectionSubtitle>
        <Paragraph>
          Multi-step "Choose Your Plan" onboarding after account creation or first successful scan:
        </Paragraph>
        <List>
          <ListItem><strong>Step 1:</strong> "What are you hoping to fix?" (multiple choice chips)</ListItem>
          <ListItem><strong>Step 2:</strong> "How intense do you want your glow-up?" (tier preference)</ListItem>
          <ListItem><strong>Step 3:</strong> Show personalized plan recommendation + full comparison</ListItem>
        </List>

        <SectionSubtitle>Pricing Page Optimization</SectionSubtitle>
        <List>
          <ListItem>Mark Plus as "Most Popular" with visual emphasis</ListItem>
          <ListItem>Mark Elite as "Best for serious skin journeys"</ListItem>
          <ListItem>Show current plan badge if user is logged in</ListItem>
          <ListItem>Monthly/yearly toggle with savings callout</ListItem>
          <ListItem>Clear feature comparison table</ListItem>
          <ListItem>Social proof (testimonials, user count)</ListItem>
        </List>

        <SectionSubtitle>Retention Strategies</SectionSubtitle>
        <List>
          <ListItem>7-day free trial on all paid plans (no credit card required)</ListItem>
          <ListItem>Email reminders before trial ends</ListItem>
          <ListItem>Win-back campaigns for churned users</ListItem>
          <ListItem>Annual plan discounts to improve LTV</ListItem>
          <ListItem>Family plan option (Elite) to reduce churn</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>7. Success Metrics</SectionTitle>
        <List>
          <ListItem><strong>Free-to-Paid Conversion:</strong> Target 12-18% within first 30 days</ListItem>
          <ListItem><strong>Plus Adoption:</strong> Target 60-70% of paid conversions</ListItem>
          <ListItem><strong>Pro Adoption:</strong> Target 25-30% of paid conversions</ListItem>
          <ListItem><strong>Elite Adoption:</strong> Target 5-10% of paid conversions</ListItem>
          <ListItem><strong>Monthly Churn:</strong> Target &lt;5% for paid plans</ListItem>
          <ListItem><strong>Annual Plan Adoption:</strong> Target 40-50% of paid users</ListItem>
          <ListItem><strong>LTV:</strong> Target $50-100 per paid user (varies by tier)</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>8. Future Considerations</SectionTitle>
        <Paragraph>
          <strong>Student Discount:</strong> Consider 50% off Plus/Pro for verified students (future feature)
        </Paragraph>
        <Paragraph>
          <strong>Corporate Wellness:</strong> B2B offering for employers (future expansion)
        </Paragraph>
        <Paragraph>
          <strong>One-Time Purchases:</strong> Consider selling individual reports or consultations (future a-la-carte options)
        </Paragraph>
        <Paragraph>
          <strong>Referral Program:</strong> Offer 1 month free for successful referrals (future feature)
        </Paragraph>
      </Section>

      <HighlightBox>
        <HighlightText>
          <strong>Note:</strong> This document should be reviewed quarterly and updated based on conversion data, user feedback, and market conditions. All pricing and features are subject to change based on business needs.
        </HighlightText>
      </HighlightBox>
    </DocumentContainer>
  );
};

export default PricingStrategyPage;



